import React, { Fragment, useEffect } from 'react';
import { UIStateProps, uiState } from '../../../atoms/uiAtom';
import { useDeleteUser, useGetUsers } from '../../../hooks/users';

import { AxiosResponse } from 'axios';
import EmptyState from '../../common/EmptyState';
import { ReactComponent as ImportantIcon } from '../../../assets/important.svg';
import { ReactComponent as InformationIcon } from '../../../assets/information.svg';
import { NumberUtils } from '../../../common/numberUtils';
import { SdpResponse } from '../../../models/SdpResponse';
import Skeleton from 'react-loading-skeleton';
import { UserResponse } from '../../../models/UserResponse';
import classNames from 'classnames';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

export interface MeterDelegatesProps {
  sdp?: SdpResponse | null;
}
export default function MeterDelegates({ sdp }: MeterDelegatesProps) {
  const { t } = useTranslation();
  const { isError, isFetching, data: delegates, refetch } = useGetUsers(sdp?.id);

  const {
    isError: isErrorDelete,
    isPending: isFetchingDelete,
    mutateAsync: deleteUser,
  } = useDeleteUser();
  const setUiState = useSetRecoilState<UIStateProps>(uiState);

  useEffect(() => {
    refetch();
  }, [refetch, sdp?.id]);

  useEffect(() => {
    setUiState((state) => ({
      ...state,
      hasError: isError,
    }));
  }, [isError, isErrorDelete, setUiState]);

  const handleClickDelete = (email: string) => {
    const sdpId = sdp!.id;
    deleteUser({ email, sdpId })
      .then(() => refetch())
      .catch(() =>
        setUiState((state) => ({
          ...state,
          hasError: true,
        }))
      );
  };

  return (
    <section className="widget meter-delegates">
      {delegates?.data.length === 0 ? (
        <EmptyState message={t('common.no.data')} icon={<ImportantIcon />} />
      ) : (
        <ul className="meter-delegates__list" data-test-id="meter-delegates-list">
          {isFetching || isError || !delegates ? (
            <Fragment>
              {[...Array(5)].map((_, index) => (
                <div key={index} className="d-flex justify-content-between flex-wrap mb-4">
                  <Skeleton height={17} width={NumberUtils.randomInteger(100, 200)} />
                  <Skeleton height={14} width={100} />
                </div>
              ))}
              <Skeleton className="mt-4" height={14} width="50%" />
            </Fragment>
          ) : (
            <Fragment>
              {(delegates as AxiosResponse<UserResponse[]>).data.map((delegate: UserResponse) => (
                <li
                  key={delegate.email}
                  className="meter-delegates__list__item"
                  data-test-id={`meter-delegate-item-${delegate.email}`}
                >
                  <div className="meter-delegates__list__item__label">
                    <span>{delegate.email}</span>
                  </div>
                  <div className="meter-delegates__list__item__action">
                    <span
                      className={classNames({ disabled: isFetchingDelete })}
                      role="button"
                      onClick={() => handleClickDelete(delegate.email)}
                    >
                      {t('common.delete')}
                    </span>
                  </div>
                </li>
              ))}
              <span className="meter-delegates__information">
                <InformationIcon className="icon me-1" />
                {t('overview.meter.detail.delegates.information')}
              </span>
            </Fragment>
          )}
        </ul>
      )}
    </section>
  );
}
